import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiDollarSign,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiPlus,
  FiImage,
  FiTag,
  FiX,
  FiMapPin,
  FiUser,
  FiPhone,
  FiMail,
  FiCreditCard,
  FiWallet,
  FiHome,
  FiTrendingUp,
  FiTrendingDown,
  FiRefreshCw,
  FiArrowUp,
  FiArrowDown,
  FiEye
} from 'react-icons/fi';
import {
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaFileInvoiceDollar,
  FaPlus,
  FaImage,
  FaTag,
  FaTimes,
  FaMapMarkerAlt,
  FaRulerCombined,
  FaRupeeSign,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaIdBadge,
  FaReceipt,
  FaWallet,
  FaHistory,
  FaLandmark,
  FaPercentage,
  FaGem
} from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from '../../utils';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';

const Payments = () => {
  const [showForm, setShowForm] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const userData = useSelector(state => state.user_info[0]?.user);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const getAllPropertiesofCurrentUser = async () => {
    setIsLoading(true);
    try {
      const response = await sendApiRequest('api/packages/all_manual/receipts', {}, 'GET');
      if (response.data.success) {
        const filteredReceipts = response.data.data.receipts
          .filter(receipt => receipt.customer.customerEmail === userData?.customerEmail && receipt.isCommissionReceipt === false)
          .map(receipt => ({
            _id: receipt._id,
            title: receipt.package.title,
            width: receipt.package.width,
            length: receipt.package.length,
            value: receipt.amount,
            paid_amount: receipt.paid_amount,
            balance_amount: receipt.balance_amount,
            location: receipt.remarks || 'Not specified',
            startDate: receipt.receiptDate,
            status: receipt.generatedManually ? 'Active' : 'Pending',
            receiptNumber: receipt.receiptNumber,
            customerName: receipt.customer.customerName,
            customerEmail: receipt.customer.customerEmail,
            customerPhone: receipt.customer.customerPhone,
            partnerName: receipt.partner.fullName,
            partnerEmail: receipt.partner.email,
            partnerPhone: receipt.partner.phone,
            paymentMode: receipt.paymentMode,
            paymentDetails: receipt.paymentDetails,
            amountInWords: receipt.amountInWords,
            createdAt: receipt.createdAt,
            updatedAt: receipt.updatedAt
          }));

        setTransactions(filteredReceipts);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to load properties',
        text: error.message,
        showConfirmButton: false,
        timer: 1500
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllPropertiesofCurrentUser();
  }, []);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Verified': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'Active': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Verified': return <FiCheckCircle className="w-3 h-3" />;
      case 'Rejected': return <FiX className="w-3 h-3" />;
      case 'Active': return <FiCheckCircle className="w-3 h-3" />;
      case 'Pending': return <FiClock className="w-3 h-3" />;
      default: return null;
    }
  };

  const toggleTransactionDetails = (id) => {
    setExpandedTransaction(expandedTransaction === id ? null : id);
  };

  const totalPaid = transactions.reduce((sum, txn) => sum + (txn.paid_amount || 0), 0);
  const totalBalance = transactions.reduce((sum, txn) => sum + (txn.balance_amount || 0), 0);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FaGem className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Payments & Transactions
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">View and manage all your payment transactions</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={getAllPropertiesofCurrentUser}
                className="p-2.5 bg-white rounded-xl shadow-md hover:shadow-lg transition text-gray-600 hover:text-indigo-600"
                title="Refresh"
              >
                <FiRefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalPaid)}</p>
                <p className="text-xs text-gray-400 mt-1">Across all properties</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <FaWallet className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Balance Due</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalBalance)}</p>
                <p className="text-xs text-gray-400 mt-1">Remaining amount</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                <FiDollarSign className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Properties</p>
                <p className="text-3xl font-bold text-purple-600">{transactions.length}</p>
                <p className="text-xs text-gray-400 mt-1">Active properties</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                <FaLandmark className="w-7 h-7 text-white" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiFileText className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-gray-800">Payment History</h3>
              </div>
              <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                {transactions.length} transactions
              </span>
            </div>
          </div>

          {transactions.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="flex flex-col items-center justify-center">
                <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <FiHome className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 font-medium text-lg">No transactions found</p>
                <p className="text-sm text-gray-400 mt-1">Your payments will appear here</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Property</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Receipt</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <AnimatePresence>
                    {transactions.map((txn, index) => (
                      <React.Fragment key={txn._id}>
                        <motion.tr
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ delay: index * 0.02 }}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => toggleTransactionDetails(txn._id)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                                <FaLandmark className="w-5 h-5 text-indigo-500" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-800">{txn.title}</p>
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <FiDollarSign className="w-3 h-3" />
                                  {txn.width} x {txn.length} ft
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <p className="text-sm font-mono text-indigo-600">{txn.receiptNumber}</p>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <FiCalendar className="w-3 h-3" />
                                {formatDate(txn.startDate)}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <p className="text-sm font-bold text-emerald-600">{formatCurrency(txn.paid_amount)}</p>
                              <p className="text-xs text-gray-500">Balance: {formatCurrency(txn.balance_amount)}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(txn.status)}`}>
                              {getStatusIcon(txn.status)}
                              {txn.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleTransactionDetails(txn._id); }}
                              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-indigo-100 hover:text-indigo-600 transition"
                            >
                              <FiEye className="w-4 h-4" />
                            </button>
                          </td>
                        </motion.tr>

                        {/* Expanded Details */}
                        {expandedTransaction === txn._id && (
                          <motion.tr
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <td colSpan="5" className="px-6 py-4 bg-gray-50">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                    <FiUser className="w-4 h-4 text-indigo-500" />
                                    Customer Details
                                  </h4>
                                  <div className="bg-white rounded-xl p-4 border border-gray-100 space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Name</span>
                                      <span className="text-sm font-medium text-gray-800">{txn.customerName}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Email</span>
                                      <span className="text-sm text-gray-700">{txn.customerEmail}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Phone</span>
                                      <span className="text-sm text-gray-700">{txn.customerPhone}</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                    <FiUser className="w-4 h-4 text-indigo-500" />
                                    Partner Details
                                  </h4>
                                  <div className="bg-white rounded-xl p-4 border border-gray-100 space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Name</span>
                                      <span className="text-sm font-medium text-gray-800">{txn.partnerName}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Email</span>
                                      <span className="text-sm text-gray-700">{txn.partnerEmail}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Phone</span>
                                      <span className="text-sm text-gray-700">{txn.partnerPhone}</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                    <FiDollarSign className="w-4 h-4 text-indigo-500" />
                                    Payment Details
                                  </h4>
                                  <div className="bg-white rounded-xl p-4 border border-gray-100 space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Mode</span>
                                      <span className="text-sm font-medium capitalize text-gray-800">{txn.paymentMode}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Reference</span>
                                      <span className="text-sm text-gray-700">{txn.paymentDetails || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Amount in Words</span>
                                      <span className="text-sm text-gray-700">{txn.amountInWords}</span>
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                    <FiMapPin className="w-4 h-4 text-indigo-500" />
                                    Property Details
                                  </h4>
                                  <div className="bg-white rounded-xl p-4 border border-gray-100 space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Location</span>
                                      <span className="text-sm font-medium text-gray-800">{txn.location}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Dimensions</span>
                                      <span className="text-sm text-gray-700">{txn.width} x {txn.length} ft</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-xs text-gray-500">Total Value</span>
                                      <span className="text-sm font-bold text-indigo-600">{formatCurrency(txn.value)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </motion.tr>
                        )}
                      </React.Fragment>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Payment History Table */}
        {userData?.paymentHistory && userData.paymentHistory.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FiHome className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-semibold text-gray-800">Payment History</h3>
                </div>
                <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                  {userData.paymentHistory.length} payments
                </span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Remaining</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Payment Mode</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {userData.paymentHistory.map((payment, index) => (
                    <motion.tr
                      key={payment._id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(payment.date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-semibold text-emerald-600">{formatCurrency(payment.amount)}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {formatCurrency(payment.totalAmount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {formatCurrency(payment.remainingAmount)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          {payment.paymentMode}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {payment.description}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payments;